import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trans } from "react-i18next";

import {
  Check,
  ChevronRight,
  Globe,
  Menu,
  MessageSquare,
  Receipt,
  X,
} from "lucide-react";
import {
  FaApple,
  FaFacebook,
  FaGooglePlay,
  FaInstagram,
  FaLinkedin,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

import appMockup from "../assets/landing/app-mockup.png";
import heroFriends from "../assets/landing/hero-friends.png";

import Card from "../components/ui/Card";
import Button from "../components/ui/Button";

const SUPPORTED_LANGUAGES = [
  { code: "tr", label: "TR" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];

const normalizeLanguage = (lng) => {
  if (!lng || typeof lng !== "string") return "tr";
  const short = lng.toLowerCase().split("-")[0];
  return ["en", "tr", "fr"].includes(short) ? short : "tr";
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const langMenuRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguage] = useState("TR");

  const changeLanguage = async (lng) => {
    const normalized = normalizeLanguage(lng);
    await i18n.changeLanguage(normalized);
    localStorage.setItem("lang", normalized);
    setLanguage(normalized.toUpperCase());
    setLangOpen(false);
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const stagger = {
    visible: {
      transition: { staggerChildren: 0.1 },
    },
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("lang");
    const initialLang = normalizeLanguage(
      savedLang || i18n.resolvedLanguage || i18n.language
    );

    if (i18n.language !== initialLang) {
      i18n.changeLanguage(initialLang);
    }

    setLanguage(initialLang.toUpperCase());
  }, [i18n]);

  useEffect(() => {
    const activeLang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    setLanguage(activeLang.toUpperCase());
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const steps = useMemo(() => {
    const data = t("landing.how_it_works.steps", { returnObjects: true });
    return data && typeof data === "object" ? Object.values(data) : [];
  }, [t, i18n.language]);

  const newsItems = useMemo(() => {
    const data = t("landing.news.items", { returnObjects: true });
    return data && typeof data === "object" ? Object.values(data) : [];
  }, [t, i18n.language]);

  const faqItems = useMemo(() => {
    const data = t("landing.faq.items", { returnObjects: true });
    return data && typeof data === "object" ? Object.values(data) : [];
  }, [t, i18n.language]);

  return (
    <div className="landing-page min-h-screen bg-background text-foreground overflow-x-hidden">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 bg-background border-b border-border ${
          scrolled ? "shadow-lg" : ""
        }`}
      >
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <span className="font-serif font-bold text-xl tracking-tight text-foreground">
              bas-bin
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 font-medium text-sm">
            <a
              href="#how-it-works"
              className="hover:text-primary transition-colors"
              data-testid="nav-how-it-works"
            >
              {t("landing.nav.how_it_works")}
            </a>
            <a
              href="#news"
              className="hover:text-primary transition-colors"
              data-testid="nav-news"
            >
              {t("landing.nav.news")}
            </a>
            <a
              href="#faq"
              className="hover:text-primary transition-colors"
              data-testid="nav-faq"
            >
              {t("landing.nav.faq")}
            </a>
            <a
              href="#support"
              className="hover:text-primary transition-colors"
              data-testid="nav-support"
            >
              {t("landing.nav.support")}
            </a>
          </div>

          <div className="flex items-center gap-3 flex-1 justify-end">
            <div ref={langMenuRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setLangOpen((open) => !open)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  fontWeight: 500,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                }}
                aria-label="Select language"
                data-testid="nav-language-toggle"
              >
                <Globe className="w-5 h-5" />
                <span>{language}</span>
              </button>

              {langOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "120%",
                    right: 0,
                    background: "white",
                    border: "1px solid #ddd",
                    borderRadius: "10px",
                    padding: "6px 0",
                    minWidth: "90px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                    zIndex: 100,
                  }}
                >
                  {SUPPORTED_LANGUAGES.map((lng) => (
                    <button
                      key={lng.code}
                      type="button"
                      onClick={() => changeLanguage(lng.code)}
                      data-testid={`nav-language-${lng.code}`}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: "14px",
                        border: "none",
                        background: "transparent",
                        fontWeight: language === lng.label ? 700 : 400,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      {lng.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={() => navigate("/login")} data-testid="nav-login">
              {t("landing.nav.login")}
            </Button>

            <button
              className="md:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border px-6 py-4 flex flex-col gap-4 font-medium text-sm">
            <a
              href="#how-it-works"
              className="hover:text-primary transition-colors py-1"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-mobile-how-it-works"
            >
              {t("landing.nav.how_it_works")}
            </a>
            <a
              href="#news"
              className="hover:text-primary transition-colors py-1"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-mobile-news"
            >
              {t("landing.nav.news")}
            </a>
            <a
              href="#faq"
              className="hover:text-primary transition-colors py-1"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-mobile-faq"
            >
              {t("landing.nav.faq")}
            </a>
            <a
              href="#support"
              className="hover:text-primary transition-colors py-1"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-mobile-support"
            >
              {t("landing.nav.support")}
            </a>

            <Button
              className="rounded-full shadow-lg w-full mt-1"
              data-testid="nav-mobile-login"
              onClick={() => {
                setMobileMenuOpen(false);
                navigate("/login");
              }}
            >
              {t("landing.nav.login")}
            </Button>
          </div>
        )}
      </nav>

      <main className="pt-20">
        <section className="relative pt-24 pb-32 overflow-hidden">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={stagger}
                className="max-w-2xl"
              >

                <motion.h1
                  variants={fadeIn}
                  className="text-4xl lg:text-5xl font-bold font-serif leading-[1.1] tracking-tight mb-6 bg-gradient-to-b from-foreground via-primary to-primary/40 bg-clip-text text-transparent"
                >
                  <Trans
                    i18nKey="landing.hero.title"
                    components={{ br: <br /> }}
                  />
                </motion.h1>

                <motion.p
                  variants={fadeIn}
                  className="text-xl text-muted-foreground mb-8 leading-relaxed max-w-lg"
                >
                  {t("landing.hero.subtitle")}
                </motion.p>

                <motion.div
                  variants={fadeIn}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Button
                    size="lg"
                    onClick={() => navigate("/register")}
                    data-testid="hero-cta-main"
                  >
                    {t("landing.hero.cta_primary")}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>

                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() =>
                      document
                        .getElementById("how-it-works")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    data-testid="hero-cta-secondary"
                  >
                    {t("landing.hero.cta_secondary")}
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative lg:h-[600px] flex items-center justify-center"
              >
                <div className="relative z-10 w-full max-w-[400px]">
                  <img
                    src={appMockup}
                    alt="bas-bin App Mockup"
                    className="w-full h-auto drop-shadow-2xl"
                  />
                </div>

                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 4,
                    ease: "easeInOut",
                  }}
                  className="absolute top-20 right-10 z-20 bg-background rounded-2xl p-4 shadow-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      <Receipt className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold">
                        {t("landing.hero.floating_card_1_title")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("landing.hero.floating_card_1_desc")}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 5,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                  className="absolute bottom-32 left-0 z-20 bg-background rounded-2xl p-4 shadow-xl border border-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold">
                        {t("landing.hero.floating_card_2_title")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("landing.hero.floating_card_2_desc")}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="py-24 bg-secondary relative overflow-hidden"
        >
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src={heroFriends}
                  alt="Friends looking at app"
                  className="w-full rounded-3xl shadow-2xl"
                />
              </motion.div>

              <div>
                <h2 className="text-4xl font-serif font-bold mb-6">
                  {t("landing.how_it_works.title")}
                </h2>
                <p className="text-lg text-muted-foreground mb-10">
                  {t("landing.how_it_works.subtitle")}
                </p>

                <div className="space-y-8">
                  {steps.map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                        {i + 1}
                      </div>
                      <div>
                        <h4 className="text-xl font-bold mb-1">{item.title}</h4>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="news" className="py-24 bg-background">
          <div className="container mx-auto px-6">
            <div className="mb-14 max-w-2xl">
              <h2 className="text-4xl font-serif font-bold mb-3">
                {t("landing.news.title")}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t("landing.news.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {newsItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group flex flex-col bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  data-testid={`news-card-${i}`}
                >
                  <div className="bg-secondary/40 h-2 w-full" />
                  <div className="p-7 flex flex-col gap-3 flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                        {item.tag}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {item.date}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold leading-snug group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                      {item.excerpt}
                    </p>
                    <a
                      href="#"
                      className="text-sm font-semibold text-primary hover:underline mt-2 self-start"
                      data-testid={`news-read-more-${i}`}
                    >
                      {t("landing.news.read_more")}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="py-24 bg-secondary">
          <div className="container mx-auto px-6 max-w-3xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif font-bold mb-4">
                {t("landing.faq.title")}
              </h2>
            </div>

            <div>
              {faqItems.map((faq, i) => (
                <div
                  key={i}
                  style={{ padding: "16px 0", borderBottom: "1px solid #ddd" }}
                >
                  <h3 style={{ margin: "0 0 8px 0" }}>{faq.question}</h3>
                  <p style={{ margin: 0 }}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="support"
          className="py-24 bg-card border-t border-border relative overflow-hidden"
        >
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 max-w-5xl mx-auto">
              <div>
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h2 className="text-4xl font-serif font-bold mb-4">
                  {t("landing.support.title")}
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  {t("landing.support.subtitle")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span>{t("landing.support.stats.response_time")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span>{t("landing.support.stats.humans")}</span>
                  </div>
                </div>
              </div>

              <Card className="border-border shadow-xl">
                <div className="p-8">
                  {isSubmitted ? (
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          margin: "0 auto 24px",
                          borderRadius: "9999px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "hsla(var(--primary), 0.1)",
                          color: "hsl(var(--primary))",
                        }}
                      >
                        <Check className="w-10 h-10" />
                      </div>
                      <h3 className="text-2xl font-serif font-bold mb-2">
                        {t("landing.support.form.success_title")}
                      </h3>
                      <p className="text-muted-foreground mb-6">
                        {t("landing.support.form.success_desc")}
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setIsSubmitted(false)}
                      >
                        {t("landing.support.form.send_another")}
                      </Button>
                    </div>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setIsSubmitted(true);
                      }}
                      style={{ display: "grid", gap: "16px" }}
                    >
                      <input
                        type="text"
                        placeholder={t("landing.support.form.name_placeholder")}
                      />
                      <input
                        type="email"
                        placeholder={t("landing.support.form.email_placeholder")}
                      />
                      <select defaultValue="">
                        <option value="" disabled>
                          {t("landing.support.form.topic_placeholder")}
                        </option>
                        <option value="General Question">
                          {t("landing.support.form.topics.general")}
                        </option>
                        <option value="Bug Report">
                          {t("landing.support.form.topics.bug")}
                        </option>
                        <option value="Feature Request">
                          {t("landing.support.form.topics.feature")}
                        </option>
                        <option value="Billing">
                          {t("landing.support.form.topics.billing")}
                        </option>
                      </select>
                      <textarea
                        placeholder={t("landing.support.form.message_placeholder")}
                        rows={5}
                        style={{ resize: "vertical" }}
                      />
                      <Button type="submit">
                        {t("landing.support.form.submit")}
                      </Button>
                    </form>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </section>

        <footer className="bg-foreground text-background py-12">
          <div className="container mx-auto px-6">
            <div className="footer-cells">
              <div className="cell cell-1-1">{t("landing.footer.company")}</div>
              <a href="#" className="cell cell-2-1" data-testid="footer-about-us">
                {t("landing.footer.about")}
              </a>
              <a href="#" className="cell cell-3-1" data-testid="footer-terms">
                {t("landing.footer.terms")}
              </a>
              <a href="#" className="cell cell-4-1" data-testid="footer-legal">
                {t("landing.footer.legal")}
              </a>

              <div className="cell cell-2-2">
                {t("landing.footer.powered_by", { company: "Basbin Akıllı Ulaşım Sistemleri A.Ş." })}
              </div>
              <div className="cell cell-3-2">{t("landing.footer.rights")}</div>

              <div className="cell cell-1-3">{t("landing.footer.contact")}</div>

              <div className="cell cell-2-3 footer-icons-pair">
                <a href="#" aria-label="LinkedIn" data-testid="footer-linkedin">
                  <FaLinkedin className="w-5 h-5" />
                </a>
                <a href="#" aria-label="Twitter / X" data-testid="footer-twitter">
                  <FaXTwitter className="w-5 h-5" />
                </a>
              </div>

              <div className="cell cell-3-3 footer-icons-pair">
                <a href="#" aria-label="Instagram" data-testid="footer-instagram">
                  <FaInstagram className="w-5 h-5" />
                </a>
                <a href="#" aria-label="Facebook" data-testid="footer-facebook">
                  <FaFacebook className="w-5 h-5" />
                </a>
              </div>

              <div className="cell cell-4-3 footer-icons-pair">
                <a href="#" aria-label="App Store" data-testid="footer-appstore">
                  <FaApple className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  aria-label="Google Play"
                  data-testid="footer-googleplay"
                >
                  <FaGooglePlay className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}